import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { portfolioProjects } from "@/i18n/translations";
import ScrollReveal from "@/components/ScrollReveal";
import PortfolioHero from "@/components/hero/PortfolioHero";

const Portfolio = () => {
  const { t, lang } = useLang();
  const [active, setActive] = useState(t.portfolio.filters[0]);
  const filterRef = useRef(null);

  const filtered =
    active === t.portfolio.filters[0]
      ? portfolioProjects
      : portfolioProjects.filter((p) => p.tags.includes(active));

  const scrollToFilters = () => {
    filterRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main data-testid="portfolio-page" className="bg-brand-bg text-white pt-32">
      <PortfolioHero onFilter={scrollToFilters} />

      <section ref={filterRef} className="border-b border-brand-border sticky top-20 z-30 bg-brand-bg/85 backdrop-blur-xl">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-5 flex flex-wrap items-center gap-3">
          {t.portfolio.filters.map((f) => {
            const isActive = active === f;
            return (
              <button
                key={f}
                onClick={() => setActive(f)}
                data-testid={`portfolio-filter-${f.toLowerCase()}`}
                className={`text-xs font-mono uppercase tracking-[0.2em] px-4 py-2 rounded-full transition-all ${
                  isActive
                    ? "btn-holo text-white"
                    : "border border-brand-border text-zinc-300 hover:border-brand-purple hover:text-white"
                }`}
              >
                {f}
              </button>
            );
          })}
        </div>
      </section>

      <section className="py-20 md:py-28 border-b border-brand-border">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          {filtered.map((p, i) => (
            <ScrollReveal
              key={p.slug}
              delay={i * 80}
              className="group relative overflow-hidden rounded-2xl border border-brand-border bg-brand-surface bento-cell card-glow"
            >
              <Link to="/contact" data-testid={`portfolio-page-card-${p.slug}`} className="block">
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-6 md:p-8 flex items-end justify-between gap-4">
                  <div>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {p.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-[0.2em] text-brand-purpleLight border border-brand-purple/40"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500 mb-1">
                      {p.category}
                    </p>
                    <h3 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight">
                      {p.name}
                    </h3>
                    <p className="mt-3 text-sm text-zinc-400 leading-relaxed max-w-md">
                      {p.description[lang]}
                    </p>
                    <p className="mt-4 text-sm text-white font-mono font-semibold">
                      {p.result[lang]}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-purple to-brand-pink grid place-items-center group-hover:scale-110 transition-transform shrink-0">
                    <ArrowUpRight className="w-5 h-5 text-white" strokeWidth={2.5} />
                  </div>
                </div>
              </Link>
            </ScrollReveal>
          ))}
        </div>
      </section>
    </main>
  );
};

export default Portfolio;
